import React from 'react';
import { Service } from '../types';
import { Clock, Wallet, ArrowLeft, Sparkles, Check } from 'lucide-react';

interface ServiceDetailProps {
  service: Service;
  onBack: () => void;
  onBook: (id: string) => void;
}

const ServiceDetail: React.FC<ServiceDetailProps> = ({ service, onBack, onBook }) => {
  return (
    <div className="animate-fade-in min-h-screen bg-slate-950 pt-20">
      {/* Cinematic Header */}
      <div className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex flex-col justify-end pb-12">
          <button 
            onClick={onBack}
            className="group mb-8 flex items-center gap-2 text-indigo-400 hover:text-amber-400 transition-all text-xs uppercase tracking-widest font-bold"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Offerings
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-12 bg-amber-500" />
            <span className="text-amber-500 text-xs uppercase tracking-[0.4em] font-bold">Divine Service</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-serif text-slate-50 mb-6 drop-shadow-2xl">
            {service.title}
          </h1>
          
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-indigo-200 bg-indigo-950/40 border border-indigo-900/50 px-4 py-2 rounded-full">
              <Clock size={16} className="text-amber-500" />
              <span className="font-medium">{service.duration} Session</span>
            </div>
            <div className="flex items-center gap-2 text-indigo-200 bg-indigo-950/40 border border-indigo-900/50 px-4 py-2 rounded-full">
              <Wallet size={16} className="text-amber-500" />
              <span className="font-medium">${service.price} Investment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8 space-y-12">
            <div className="relative">
              <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500/50 to-transparent" />
              <p className="text-2xl md:text-3xl text-indigo-100 font-light italic leading-relaxed pl-8">
                {service.description}
              </p>
            </div>
            
            <div className="space-y-6 text-indigo-200/80 text-lg leading-relaxed font-light">
              <h3 className="text-xl font-serif text-white uppercase tracking-widest">The Soul Alignment</h3>
              <p>{service.longDescription}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 pt-12">
              <div className="p-8 bg-slate-900/50 border border-indigo-900/50 rounded-sm">
                <h4 className="text-amber-400 text-[10px] uppercase tracking-widest font-bold mb-6">What to expect</h4>
                <ul className="space-y-4 text-sm text-indigo-100/70">
                  <li className="flex gap-3"><Check size={16} className="text-amber-500 flex-shrink-0" /> Personalized audio session via phone.</li>
                  <li className="flex gap-3"><Check size={16} className="text-amber-500 flex-shrink-0" /> Deep exploration of your chart geometry.</li>
                  <li className="flex gap-3"><Check size={16} className="text-amber-500 flex-shrink-0" /> Actionable cosmic advice for current transits.</li>
                </ul>
              </div>
              <div className="p-8 bg-indigo-950/20 border border-amber-500/10 rounded-sm">
                <h4 className="text-amber-400 text-[10px] uppercase tracking-widest font-bold mb-6">Preparation</h4>
                <p className="text-sm text-indigo-100/70 italic leading-relaxed">
                  "Please ensure you have your exact birth time (from a birth certificate if possible) and a quiet, sacred space for our conversation."
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-32 p-10 bg-slate-900 border border-amber-500/20 rounded-sm shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                 <Sparkles size={100} className="text-amber-400" />
              </div>
              
              <h3 className="text-2xl font-serif text-white mb-4">Book this Alignment</h3>
              <p className="text-sm text-indigo-300 mb-8">Ready to consult the heavens? Add this service to your session and select a time that honors your current cycle.</p>
              
              <div className="space-y-4 mb-10">
                <div className="flex justify-between items-center text-sm border-b border-indigo-900/50 pb-4">
                  <span className="text-indigo-400 uppercase tracking-widest text-[10px] font-bold">Investment</span>
                  <span className="text-white font-serif text-2xl">${service.price}</span>
                </div>
              </div>

              <button 
                onClick={() => onBook(service.id)}
                className="w-full py-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-[0.2em] text-xs transition-all shadow-lg rounded-sm flex items-center justify-center gap-3"
              >
                Schedule Now <Sparkles size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;